import { useState, type ReactNode } from 'react';

import { ApiError, NetworkError } from '../api/client.js';
import type { Discovery, FeedCandidate } from '../api/types.js';

/**
 * Adding one feed (M2.6.2).
 *
 * ## It asks for an address, not a feed URL
 *
 * People think in websites. The discovery in M1.8.1 turns a home page into a
 * feed, so asking somebody for "the RSS URL" is asking them to know something
 * they should not have to — R1.3, and the placeholder says so.
 *
 * ## When a page offers several feeds, it asks which
 *
 * The part that did not exist before. Discovery has returned candidates in
 * preference order since M1.8.1 and `POST /subscriptions` takes the first one
 * that parses — which is right for a blog and quietly wrong for a newspaper,
 * where the first is the firehose and the one you wanted was the section feed
 * three rows down. So: one candidate subscribes immediately, several are
 * shown, and the preferred one is pre-selected so the common case is still
 * one click.
 *
 * The cost is one extra fetch of the page. Set against subscribing somebody
 * to the wrong feed and leaving them to work out why, it is worth it.
 *
 * ## Nothing declared means no question, not an error
 *
 * The discovery endpoint answers with the feeds a page *declares*, and plenty
 * of sites declare none while still having one at a conventional address. An
 * empty answer therefore falls straight through to an ordinary subscribe,
 * which tries those addresses and verifies them — exactly what happened
 * before this flow existed. The user sees one button doing one thing either
 * way.
 *
 * ## The folder is chosen here, not afterwards
 *
 * Somebody adding a feed usually knows where it goes, and making them find it
 * in the sidebar and drag it is a second task for something they already knew.
 */

export interface AddFeedProps {
  readonly folders: readonly { folderId: string; name: string }[];
  readonly onDiscover: (url: string) => Promise<Discovery>;
  readonly onSubscribe: (url: string, folderId?: string) => Promise<void>;
  readonly onClose: () => void;
  /** Offers the import screen instead, since a migrator is in the wrong place. */
  readonly onImport?: () => void;
}

type Stage =
  | { readonly kind: 'address' }
  | { readonly kind: 'choose'; readonly discovery: Discovery };

export function AddFeed(props: AddFeedProps): ReactNode {
  const [url, setUrl] = useState('');
  const [folderId, setFolderId] = useState('');
  const [stage, setStage] = useState<Stage>({ kind: 'address' });
  const [chosen, setChosen] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const message = (caught: unknown, fallback: string): string =>
    caught instanceof ApiError
      ? caught.userMessage
      : caught instanceof NetworkError
        ? 'The service could not be reached. Try again in a moment.'
        : fallback;

  const subscribe = async (target: string): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      await props.onSubscribe(target, folderId === '' ? undefined : folderId);
      props.onClose();
    } catch (caught) {
      setError(message(caught, 'That feed could not be added.'));
    } finally {
      setBusy(false);
    }
  };

  const find = async (): Promise<void> => {
    const trimmed = url.trim();
    if (trimmed === '') {
      return;
    }

    setBusy(true);
    setError(undefined);

    try {
      const discovery = await props.onDiscover(trimmed);

      /*
       * Nothing declared: subscribe to what they typed and let the server try
       * the conventional addresses, which it verifies. Asking "which of these
       * nought feeds?" would be absurd, and refusing would make this flow
       * worse at finding feeds than typing the URL used to be.
       */
      if (discovery.candidates.length === 0) {
        await subscribe(trimmed);
        return;
      }

      /*
       * One candidate is not a choice, so it is not presented as one. The
       * direct case — somebody pasted a feed URL — lands here too.
       */
      if (discovery.candidates.length === 1) {
        const only = discovery.candidates[0];
        if (only !== undefined) {
          await subscribe(only.url);
          return;
        }
      }

      setChosen(discovery.candidates[0]?.url);
      setStage({ kind: 'choose', discovery });
    } catch (caught) {
      setError(message(caught, 'Nothing could be read at that address.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Add a feed">
      <div className="overlay__card">
        <h2 className="overlay__title">Add a feed</h2>

        {stage.kind === 'address' ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void find();
            }}
          >
            <label className="field">
              <span className="field__label">Address</span>
              <input
                className="field__input"
                type="text"
                value={url}
                placeholder="example.com — or the feed URL, if you have it"
                autoFocus
                disabled={busy}
                onChange={(event) => {
                  setUrl(event.target.value);
                }}
              />
            </label>

            <FolderField
              folders={props.folders}
              value={folderId}
              disabled={busy}
              onChange={setFolderId}
            />

            {error !== undefined && (
              <p className="auth__error" role="alert">
                {error}
              </p>
            )}

            <div className="overlay__actions">
              <button className="button button--primary" type="submit" disabled={busy}>
                {busy ? 'Looking…' : 'Add'}
              </button>
              <button className="button" type="button" onClick={props.onClose}>
                Cancel
              </button>
            </div>

            {props.onImport !== undefined && (
              <p className="overlay__aside">
                Moving from another reader?{' '}
                <button className="link" type="button" onClick={props.onImport}>
                  Import your whole list
                </button>
                .
              </p>
            )}
          </form>
        ) : (
          <>
            <p className="overlay__text">
              {stage.discovery.candidates.length} feeds here. Which one did you want?
            </p>

            <ul className="candidates">
              {stage.discovery.candidates.map((candidate) => (
                <li key={candidate.url}>
                  <label className="candidates__row">
                    <input
                      type="radio"
                      name="candidate"
                      checked={chosen === candidate.url}
                      onChange={() => {
                        setChosen(candidate.url);
                      }}
                    />
                    <span className="candidates__title">
                      {candidate.title ?? describe(candidate)}
                    </span>
                    <span className="candidates__url">{candidate.url}</span>
                  </label>
                </li>
              ))}
            </ul>

            <FolderField
              folders={props.folders}
              value={folderId}
              disabled={busy}
              onChange={setFolderId}
            />

            {error !== undefined && (
              <p className="auth__error" role="alert">
                {error}
              </p>
            )}

            <div className="overlay__actions">
              <button
                className="button button--primary"
                type="button"
                disabled={busy || chosen === undefined}
                onClick={() => {
                  if (chosen !== undefined) {
                    void subscribe(chosen);
                  }
                }}
              >
                {busy ? 'Adding…' : 'Add this one'}
              </button>
              <button
                className="button"
                type="button"
                disabled={busy}
                onClick={() => {
                  setStage({ kind: 'address' });
                  setError(undefined);
                }}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FolderField(props: {
  readonly folders: readonly { folderId: string; name: string }[];
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (folderId: string) => void;
}): ReactNode {
  if (props.folders.length === 0) {
    // Nothing to choose from, and an empty dropdown is a question with no
    // answers — the feed lands unfiled, which is where it would have gone.
    return null;
  }

  return (
    <label className="field">
      <span className="field__label">Folder</span>
      <select
        className="field__input"
        value={props.value}
        disabled={props.disabled}
        onChange={(event) => {
          props.onChange(event.target.value);
        }}
      >
        <option value="">No folder</option>
        {props.folders.map((folder) => (
          <option key={folder.folderId} value={folder.folderId}>
            {folder.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * A name for a candidate the page did not name.
 *
 * Better than showing the URL twice: `source` says where it came from, and
 * "the site's usual feed address" is a meaningfully different thing to be
 * offered than "a feed this page links to".
 */
function describe(candidate: FeedCandidate): string {
  switch (candidate.source) {
    case 'link-element':
      return 'A feed this page declares';
    case 'anchor':
      return 'A feed this page links to';
    case 'convention':
      return "The site's usual feed address";
    default:
      return 'A feed';
  }
}
